import { PurchaseOrderSummary } from '../../../types/procurement.types';

/**
 * Small pure helpers shared by every Procurement screen. Money and dates are formatted in one
 * place so a list row and its detail page can never disagree about the same number.
 */

const AUD = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/** Prices are ex GST, AUD — the prototype's own caption. */
export const money = (value: number | null | undefined) =>
    AUD.format(Number(value ?? 0));

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Date-only values arrive as `YYYY-MM-DD`; render them without a timezone shift. */
export const formatDate = (value: string | null | undefined) => {
    if (!value) return '—';
    const datePart = String(value).slice(0, 10).split('-');
    if (datePart.length !== 3) return String(value);
    return `${Number(datePart[2])} ${MONTHS[Number(datePart[1]) - 1]} ${datePart[0]}`;
};

export const formatDateTime = (value: string | null | undefined) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return formatDate(value);
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ` +
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Whole days between a timestamp and now — "3 days waiting" on the approvals queue. */
export const daysAgo = (value: string | null | undefined) => {
    if (!value) return 0;
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) return 0;
    return Math.max(0, Math.round((Date.now() - then) / 86_400_000));
};

/** `PO-SYD-00041` once issued, `REQ-000114` before that. */
export const orderReference = (order: Pick<PurchaseOrderSummary, 'reference' | 'poNumber'>) =>
    order.poNumber || order.reference;

export const vendorName = (vendor: { legalName: string; tradingName: string | null } | null) =>
    vendor ? (vendor.tradingName || vendor.legalName) : '—';

/** Clamp a quantity so a receipt can never exceed what is still outstanding. */
export const clampQuantity = (value: number, max: number) =>
    Math.max(0, Math.min(Number.isFinite(value) ? value : 0, max));
